import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import Gallery from './Gallery';
import { useSiteSettings } from './lib/useSiteSettings';
import './App.css';

// Code-splitting : ces routes ne sont chargées qu'à la navigation,
// ce qui allège le bundle JS initial de la page d'accueil.
const Carte = lazy(() => import('./Carte'));
const Reservation = lazy(() => import('./Reservation'));
const Admin = lazy(() => import('./admin'));

function useScrollAnimations() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useScrollAnimations();
  const settings = useSiteSettings();

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      {/* NAV */}
      <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
        <Link to="/" className="nav__logo">Le Petit Bruxelles</Link>
        <button
          className="nav__burger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span />
          <span />
          <span />
        </button>
        <ul className={`nav__links ${menuOpen ? 'nav__links--open' : ''}`}>
          <li><a href="#accueil" onClick={closeMenu}>Accueil</a></li>
          <li><a href="#histoire" onClick={closeMenu}>Notre Histoire</a></li>
          <li><Link to="/carte" onClick={closeMenu}>La Carte</Link></li>
          <li><a href="#evenements" onClick={closeMenu}>Événements</a></li>
          <li><a href="#horaires" onClick={closeMenu}>Horaires</a></li>
          <li><a href="#contact" onClick={closeMenu}>Contact</a></li>
          <li>
            <a
              href="https://bookings.zenchef.com/results?rid=375809"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--color-gold)' }}
            >
              Réservation
            </a>
          </li>
        </ul>
      </nav>

      {/* HERO */}
      <section id="accueil" className="hero">
        <div className="hero__bg" />
        <p className="hero__subtitle hero__animate hero__animate--1">Votre restaurant à Sainte-Marie-Cappel</p>
        <h1 className="hero__title hero__animate hero__animate--2">Brasserie du Petit Bruxelles</h1>
        <p className="hero__tagline hero__animate hero__animate--3">
          Au cœur des Flandres, découvrez une cuisine flamande authentique dans un cadre exceptionnel.
        </p>
        <li>
            <a
              href="https://bookings.zenchef.com/results?rid=375809"
              target="_blank"
              rel="noopener noreferrer"
              className="reservation__btn"
            >
              Réserver une table
            </a>
          </li>
        <a href="#a-la-une" className="hero__badge hero__animate hero__animate--4">
          <span aria-hidden="true">🎉</span> Réveillon 2026 · Le 31 by Tres Cuatro
        </a>
      </section>

      {/* À LA UNE : soirée du 31 décembre */}
      <section id="a-la-une" className="section featured">
        <div className="featured__grid">
          <a
            className="featured__poster animate-on-scroll"
            href="https://bookings.zenchef.com/results?rid=375809"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Réserver pour la soirée du 31 décembre 2026 avec Los Tres Cuatro"
          >
            <img
              src="/images/le-31-by-tres-cuatro-2026.jpg"
              alt="Affiche : Le 31 by Tres Cuatro, soirée du réveillon 2026 à la Brasserie du Petit Bruxelles"
              width={1080}
              height={1350}
              loading="lazy"
              decoding="async"
            />
          </a>
          <div className="featured__content animate-on-scroll delay-2">
            <p className="section__label">À l'affiche</p>
            <h2>Le 31 by Tres Cuatro !</h2>
            <div className="section__separator" />
            <p className="featured__date">Réveillon du 31 décembre 2026</p>
            <p className="featured__text">
              Venez participer à la plus belle des soirées pour conclure 2026 ! Un menu de fête
              accompagné d'un groupe mythique pour l'animer : Los Tres Cuatro, au cœur des Flandres.
            </p>
            <p className="featured__text featured__text--highlight">
              De subtils accords de salsa, de musiques cubaines ou caribéennes, de cumbia, avec une
              influence de Buena Vista Social Club et de bonnes surprises. La nuit s'annonce
              « MUCHO CALIENTE » !
            </p>
            <p className="featured__text featured__text--strong">
              Pensez à réserver, nous nous occupons du reste.
            </p>
            <div className="featured__actions">
              <a
                href="https://bookings.zenchef.com/results?rid=375809"
                target="_blank"
                rel="noopener noreferrer"
                className="reservation__btn"
              >
                Réserver ma soirée
              </a>
              <p className="featured__phones">
                <a href="tel:+33365674453">03 65 67 44 53</a>
                {' / '}
                <a href="tel:+33672790018">06 72 79 00 18</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="histoire" className="section">
        <div className="about__grid">
          <img
            className="about__image animate-on-scroll"
            src={`/images/PHOTO-2026-06-15-18-03-41 2.jpg`}
            alt="Intérieur de la Brasserie du Petit Bruxelles"
            width={675}
            height={900}
            loading="lazy"
            decoding="async"
          />
          <div className="about__content animate-on-scroll delay-2">
            <p className="section__label">Depuis plus de 100 ans</p>
            <h2>Découvrez la Brasserie du Petit Bruxelles</h2>
            <div className="section__separator" />
            <p>
              Véritable institution depuis plus de 100 ans, la Brasserie du Petit Bruxelles
              à Sainte-Marie-Cappel est un lieu emblématique du patrimoine flamand.
              Aujourd'hui rénovée avec goût et respectueuse de son histoire, elle vous
              accueille dans une ambiance chaleureuse pour vous proposer des spécialités
              flamandes et françaises préparées avec passion par notre équipe.
            </p>
            <p>
              À la carte : potjevleesch maison, welsh authentique, carbonnade flamande ou
              encore nos délicieux plats typiques du Nord. Proche de Hazebrouck, Saint-Omer
              ou encore Dunkerque, notre restaurant familial vous invite à partager un moment
              gourmand dans un cadre de charme.
            </p>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <Gallery />

      {/* SPECIALTIES PREVIEW */}
      <section id="carte" className="section section--alt">
        <p className="section__label">Notre Carte</p>
        <h2 className="section__title">Spécialités Flamandes</h2>
        <div className="section__separator" />
        <p className="section__text">
          Découvrez nos spécialités flamandes préparées avec passion, à partir de produits
          locaux soigneusement sélectionnés. Une cuisine du terroir, généreuse et authentique,
          qui vous transporte au cœur des saveurs du Nord.
        </p>
        <div className="specialties__grid">
          <div className="specialty-card animate-on-scroll delay-1">
            <div className="specialty-card__icon">🍲</div>
            <h3 className="specialty-card__title">Potjevleesch</h3>
            <p className="specialty-card__desc">
              Notre potjevleesch maison, préparé selon la tradition flamande avec quatre
              viandes en gelée, servi avec des frites croustillantes.
            </p>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <Link to="/carte" className="hero__cta">Voir la carte complète</Link>
        </div>
      </section>

      {/* EVENTS */}
      <section id="evenements" className="section">
        <p className="section__label">Événements</p>
        <h2 className="section__title">Le lieu idéal pour vos événements</h2>
        <div className="section__separator" />
        <div className="events__content animate-on-scroll">
          <p className="section__text">
            Privatisation du restaurant pour vos célébrations. Nous vous offrons un salon
            privé, une salle de réception élégante et conviviale à Sainte-Marie-Cappel.
            Faites de vos événements des moments inoubliables dans un lieu chargé d'histoire.
          </p>
          <div className="events__list">
            <span className="events__tag">Mariages</span>
            <span className="events__tag">Baptêmes</span>
            <span className="events__tag">Communions</span>
            <span className="events__tag">Séminaires</span>
            <span className="events__tag">Repas d'affaires</span>
          </div>
        </div>
      </section>

      {/* HOURS */}
      <section id="horaires" className="section section--alt">
        <p className="section__label">Horaires</p>
        <h2 className="section__title">Nos Horaires d'Ouverture</h2>
        <div className="section__separator" />
        <div className="hours__grid animate-on-scroll">
          {settings.hours.map((row) => (
            <div key={row.day} className="hours__row">
              <span className="hours__day">{row.day}</span>
              <span className={`hours__time${row.closed ? ' hours__time--closed' : ''}`}>
                {row.closed ? 'Fermé' : row.time}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* RESERVATION SECTION */}
      <section id="reservation" className="section reservation">
        <p className="section__label">Réservation</p>
        <h2 className="section__title">Réservez Votre Table</h2>
        <div className="section__separator" />
        <p className="section__text">
          Réservez facilement votre table en ligne en cliquant sur le bouton ci-dessous.
        </p>
        <Link to="/reservation" className="reservation__btn" style={{ textDecoration: 'none' }}>
          Réserver maintenant
        </Link>
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.9rem', color: '#666' }}>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="section section--alt">
        <p className="section__label">Contact</p>
        <h2 className="section__title">Nous Trouver</h2>
        <div className="section__separator" />
        <div className="contact__grid animate-on-scroll">
          <div>
            <div className="contact-item__icon">📍</div>
            <p className="contact-item__label">Adresse</p>
            <p className="contact-item__value">
              1656 Route Nationale<br />
              59670 Sainte-Marie-Cappel
            </p>
          </div>
          <div>
            <div className="contact-item__icon">📞</div>
            <p className="contact-item__label">Téléphone</p>
            <p className="contact-item__value">
              <a href="tel:+33365674453">+33 3 65 67 44 53</a>
            </p>
          </div>
          <div>
            <div className="contact-item__icon">✉️</div>
            <p className="contact-item__label">Email</p>
            <p className="contact-item__value">
              <a href="mailto:brasseriedupetitbruxelles@gmail.com">
                brasseriedupetitbruxelles@gmail.com
              </a>
            </p>
          </div>
        </div>
        <div className="contact__map">
          <iframe
            title="Carte de localisation de la Brasserie du Petit Bruxelles au 1656 Route Nationale, 59670 Sainte-Marie-Cappel"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2538.5!2d2.5369!3d50.7994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47dc3a0e0e0e0e0f%3A0x0!2s1656+Route+Nationale+59670+Sainte-Marie-Cappel!5e0!3m2!1sfr!2sfr!4v1"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <p>
          &copy; {new Date().getFullYear()} Brasserie du Petit Bruxelles — Tous droits
          réservés
        </p>
      </footer>
    </>
  );
}

function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/carte" element={<Carte />} />
        <Route path="/reservation" element={<Reservation />} />
        <Route path="/admin/*" element={<Admin />} />
      </Routes>
    </Suspense>
  );
}

export default App;